import { useState, useEffect } from 'react'
import { Mail, FileText, Users, CheckCircle, Clock, Newspaper, ExternalLink, RefreshCw, Building2, MapPin } from 'lucide-react'
import { Link } from 'react-router-dom'
import api from '@/services/api'

interface DashboardStats {
  letters: {
    pending: number
    approved: number
    rejected: number
    total: number
  }
  applications: {
    total: number
    new: number
    classified: number
  }
  clients: {
    total: number
    active: number
  }
}

interface RecentLetter {
  id: number
  clientName: string
  applicationReference: string
  status: string
  createdAt: string
}

interface RecentApplication {
  id: number
  referenceNumber: string
  propertyAddress: string
  territoryName: string
  scrapedAt: string
}

interface NewsItem {
  title: string
  link: string
  pubDate: string
  source: string
  thumbnail: string | null
  description: string
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentLetters, setRecentLetters] = useState<RecentLetter[]>([])
  const [recentApplications, setRecentApplications] = useState<RecentApplication[]>([])
  const [news, setNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [newsLoading, setNewsLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
    fetchNews()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const [lettersRes, applicationsRes, clientsRes, recentLettersRes, recentAppsRes] = await Promise.all([
        api.get('/api/v1/letters/stats'),
        api.get('/api/v1/applications/stats'),
        api.get('/api/v1/clients/'),
        api.get('/api/v1/letters/pending?limit=5'),
        api.get('/api/v1/applications/?limit=5'),
      ])

      setStats({
        letters: lettersRes.data,
        applications: applicationsRes.data,
        clients: {
          total: clientsRes.data.length,
          active: clientsRes.data.filter((c: any) => c.isActive).length,
        },
      })

      setRecentLetters(recentLettersRes.data.slice(0, 5))
      setRecentApplications(recentAppsRes.data.slice(0, 5))
    } catch {
      // Dashboard data fetch failed silently
    } finally {
      setLoading(false)
    }
  }

  const fetchNews = async () => {
    setNewsLoading(true)
    try {
      // Using RSS2JSON API to fetch construction/trades news
      const feeds = [
        { url: 'https://www.constructionnews.co.uk/feed/', source: 'Construction News' },
        { url: 'https://www.theconstructionindex.co.uk/news/feed', source: 'Construction Index' },
        { url: 'https://www.pbctoday.co.uk/news/feed/', source: 'PBC Today' },
      ]
      
      const newsItems: NewsItem[] = []
      
      for (const feed of feeds) {
        try {
          const response = await fetch(
            `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feed.url)}`
          )
          const data = await response.json()
          
          if (data.status === 'ok' && data.items) {
            data.items.slice(0, 5).forEach((item: any) => {
              newsItems.push({
                title: item.title,
                link: item.link,
                pubDate: item.pubDate,
                source: feed.source,
                thumbnail: item.thumbnail || item.enclosure?.link || null,
                description: item.description?.replace(/<[^>]*>/g, '').slice(0, 120) || '',
              })
            })
          }
        } catch {
          // Individual feed fetch failed
        }
      }
      
      // Sort by date and take top 12
      newsItems.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime())
      setNews(newsItems.slice(0, 12))
    } catch {
      // News fetch failed silently
    } finally {
      setNewsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-amber-400'
      case 'approved': return 'text-emerald-400'
      case 'rejected': return 'text-red-400'
      default: return 'text-slate-400'
    }
  }

  const statCards = [
    { 
      name: 'Letters Pending', 
      value: stats?.letters.pending ?? 0, 
      icon: Mail, 
      color: 'from-amber-500 to-orange-600',
      link: '/letters'
    },
    { 
      name: 'New Applications', 
      value: stats?.applications.new ?? 0, 
      icon: FileText, 
      color: 'from-blue-500 to-cyan-600',
      link: '/applications'
    },
    { 
      name: 'Active Clients', 
      value: stats?.clients.active ?? 0, 
      icon: Users, 
      color: 'from-emerald-500 to-teal-600',
      link: '/clients'
    },
    { 
      name: 'Approved Letters', 
      value: stats?.letters.approved ?? 0, 
      icon: CheckCircle, 
      color: 'from-purple-500 to-pink-600',
      link: '/letters'
    },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-100">Dashboard</h1>
        <p className="text-slate-400 mt-1">Welcome back! Here's what's happening today.</p>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((stat) => (
          <Link 
            key={stat.name} 
            to={stat.link}
            className="card hover:border-slate-600/50 hover:scale-[1.02] transition-all duration-200"
          >
            <div className="flex items-start justify-between">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-100">
                {loading ? <RefreshCw className="w-6 h-6 animate-spin" /> : stat.value}
              </p>
              <p className="text-sm text-slate-400 mt-1">{stat.name}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-100">Recent Letters</h2>
            <Link to="/letters" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
            </div>
          ) : recentLetters.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No recent letters to display.</p>
              <p className="text-slate-500 text-sm mt-1">Letters will appear here once generated.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentLetters.map((letter) => (
                <Link
                  key={letter.id}
                  to="/letters"
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-slate-700/50">
                      <Building2 className="w-4 h-4 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">{letter.clientName}</p>
                      <p className="text-xs text-slate-500">{letter.applicationReference}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-medium capitalize ${getStatusColor(letter.status)}`}>
                      {letter.status}
                    </span>
                    <p className="text-xs text-slate-500">{formatDate(letter.createdAt)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-100">Recent Applications</h2>
            <Link to="/applications" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
            </div>
          ) : recentApplications.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No recent applications to display.</p>
              <p className="text-slate-500 text-sm mt-1">Scraped applications will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentApplications.map((app) => (
                <Link
                  key={app.id}
                  to="/applications"
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-slate-700/50">
                      <FileText className="w-4 h-4 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-200 truncate max-w-[200px]">
                        {app.propertyAddress || app.referenceNumber}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" />
                        {app.territoryName || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{app.referenceNumber}</p>
                    <p className="text-xs text-slate-500">{formatDate(app.scrapedAt)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Industry News */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Industry News</h2>
              <p className="text-xs text-slate-500">Latest from construction & trades</p>
            </div>
          </div>
          <button 
            onClick={fetchNews}
            className="p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-colors"
            title="Refresh news"
          >
            <RefreshCw className={`w-4 h-4 ${newsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {newsLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-12">
            <Newspaper className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Unable to load news feed.</p>
            <p className="text-slate-500 text-sm mt-1">Check your connection and try again.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {news.map((item, index) => (
              <a
                key={index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex gap-3 p-3 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/30 hover:border-slate-600/50 transition-all duration-200"
              >
                {/* Thumbnail */}
                <div className="w-20 h-20 rounded-lg bg-slate-700/50 overflow-hidden flex-shrink-0">
                  <img 
                    src={item.thumbnail || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=200&fit=crop'}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200&h=200&fit=crop'
                    }}
                  />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-medium text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">
                    {item.source}
                  </span>
                  <h3 className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors line-clamp-2 mt-1.5 leading-snug">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-2">
                    <Clock className="w-3 h-3" />
                    {formatDate(item.pubDate)}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
